/**
 * Pagination is designed to now know anything about what it is paginating.
 *
 *
 * This is a pagination function, it accepts some setup values:
 *
 *
 * - currentPageNumber (1 based) - the current page (not 0 based)
 * - pageSize - the number of items per page 10, 25, 50, Infinity.
 * - numberOfRows - the total number of rows we are paginating.
 *
 * From this it can calculate:
 * - currentPageNumber (1 based) - the current page
 * - firstItemNumberOnPage (1 based) - the number of the first item on the current page
 * - firstPageNumber (1 based) - the number of the first page
 * - itemsPerPage - the number of items per page
 * - lastItemNumberOnPage (1 based) - the number of the last item on the current page
 * - lastPageNumber (1 based) - the number of the last page
 * - pageNumbers - an array of page numbers (1 based)
 * - previousPageNumber (1 based) - the number of the previous page
 * - totalItems - the total number of items
 * - totalPageCount - the total number of pages (not 0 based)
 * - hasPreviousPage - a boolean indicating if there is a previous page
 * - hasNextPage - a boolean indicating if there is a next page
 * - pageInfo - a string describing the current state of the pagination like `${firstItemNumberOnPage}-${lastItemNumberOnPage} of ${totalItems}`
 *
 */

interface PaginationProps {
  currentPageNumber: number
  pageSize: number
  numberOfRows: number
}

export interface PaginationResult {
  currentPageNumber: number
  firstItemNumberOnPage: number
  firstPageNumber: number
  itemsPerPage: number
  lastItemNumberOnPage: number
  lastPageNumber: number
  pageNumbers: number[]
  previousPageNumber: number
  nextPageNumber: number
  totalItems: number
  totalPageCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
  pageInfo: string
  indexes: {
    firstItemIndexOnPage: number
    lastItemIndexOnPage: number
  }
}

export const paginate = ({ currentPageNumber, pageSize, numberOfRows }: PaginationProps): PaginationResult => {
  if (numberOfRows === void 0) {
    numberOfRows = 0
  }

  const totalPageCount = Math.ceil(numberOfRows / pageSize)

  // if we are not on page one and we have less than one pages worth of rows, force currentPageNumber = 1
  if (currentPageNumber > 1 && numberOfRows <= pageSize) {
    currentPageNumber = 1
  }

  // Bug: "46 - 44 of 44: shows and empty page and should default back to currentPageNumber = 1;
  // were on page 10 and there are 5 rows per page, and there are 44 rows
  // then reset to the last page

  if (currentPageNumber > totalPageCount) {
    currentPageNumber = totalPageCount
  }

  const firstItemNumberOnPage = (currentPageNumber - 1) * pageSize + 1
  const lastItemNumberOnPage = Math.min(currentPageNumber * pageSize, numberOfRows)
  const pageNumbers = Array.from(Array(totalPageCount).keys()).map(function (i) {
    return i + 1
  })
  const previousPageNumber = currentPageNumber - 1
  const nextPageNumber = currentPageNumber + 1
  const hasPreviousPage = previousPageNumber >= 1
  const hasNextPage = nextPageNumber <= totalPageCount

  let pageInfo = `${Number(isNaN(firstItemNumberOnPage) ? 1 : firstItemNumberOnPage)} - ${Number(
    isNaN(lastItemNumberOnPage) ? 1 : lastItemNumberOnPage,
  )} of ${Number(numberOfRows)}`

  if (numberOfRows === 0) {
    pageInfo = "No pages available"
  }
  // lets calculate the indexes for the consumer
  // we want to return the indexes of the first and last items on the page
  const firstItemIndexOnPage = firstItemNumberOnPage - 1
  const lastItemIndexOnPage = lastItemNumberOnPage

  return {
    currentPageNumber: currentPageNumber,
    firstItemNumberOnPage: firstItemNumberOnPage,
    firstPageNumber: 1,
    itemsPerPage: pageSize,
    lastItemNumberOnPage: lastItemNumberOnPage,
    lastPageNumber: totalPageCount,
    pageNumbers: pageNumbers,
    previousPageNumber,
    nextPageNumber,
    totalItems: numberOfRows,
    totalPageCount: totalPageCount,
    hasPreviousPage: hasPreviousPage,
    hasNextPage: hasNextPage,
    pageInfo: pageInfo,
    indexes: {
      firstItemIndexOnPage,
      lastItemIndexOnPage,
    },
  }
}
